// Page Événements Mobile - Design unifié
// Header simple "Événements" + Onglets Tous/Pro/Networking + Loupe recherche fullscreen

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, X, Clock, Calendar, Plus, MapPin, Users, Loader2,
  RefreshCw, SlidersHorizontal, Briefcase, Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import UnifiedFilterSystem from '../../components/filters/UnifiedFilterSystem';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MobileEvenementsPage({ isDesktopEmbed = false }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filtres
  const [activeTab, setActiveTab] = useState('tous');
  const [showFullscreenSearch, setShowFullscreenSearch] = useState(false);
  const [showFullFilters, setShowFullFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const searchInputRef = useRef(null);
  
  useEffect(() => {
    fetchEvents();
  }, []);
  
  useEffect(() => {
    try {
      const saved = localStorage.getItem('events_recent_searches');
      if (saved) setRecentSearches(JSON.parse(saved).slice(0, 5));
    } catch {}
  }, []);
  
  useEffect(() => {
    if (showFullscreenSearch && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 250);
    }
  }, [showFullscreenSearch]);
  
  const fetchEvents = async () => {
    setLoading(true);
    try {
      // Simuler le chargement
      await new Promise(r => setTimeout(r, 500));
      setEvents([]);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchEvents();
    setRefreshing(false);
  };
  
  const saveRecentSearch = (query) => {
    if (!query || query.trim().length < 2) return;
    const trimmed = query.trim();
    const updated = [trimmed, ...recentSearches.filter(s => s.toLowerCase() !== trimmed.toLowerCase())].slice(0, 5);
    setRecentSearches(updated);
    try { localStorage.setItem('events_recent_searches', JSON.stringify(updated)); } catch {}
  };
  
  const clearRecentSearches = () => {
    setRecentSearches([]);
    try { localStorage.removeItem('events_recent_searches'); } catch {}
  };
  
  const handleSearchSubmit = (query) => {
    if (query && query.trim()) {
      saveRecentSearch(query);
      setSearchQuery(query.trim());
    }
    setShowFullscreenSearch(false);
  };

  const handleCreate = () => {
    toast.info('Connecte-toi pour créer un événement');
  };
  
  return (
    <div className={isDesktopEmbed ? "bg-white" : "min-h-screen bg-gray-50"} style={{ paddingBottom: isDesktopEmbed ? '0' : '80px' }}>
      {/* Barre Bêta + Header - Masqués en mode Desktop embed */}
      {!isDesktopEmbed && (
        <>
          {/* Bandeau bêta supprimé - géré par TopBanner.jsx global */}
          
          <header className="bg-white border-b border-gray-200 sticky top-[30px] z-50">
            <div className="flex items-center justify-between h-14 px-3">
              <div className="flex items-center gap-0.5">
                <h1 className="text-lg font-bold text-[#1E3A5F]">Événements</h1>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={handleRefresh} disabled={refreshing} className="p-2 hover:bg-gray-100 rounded-full">
                  <RefreshCw size={20} className={`text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
                <button onClick={() => setShowFullscreenSearch(true)} className="p-2 hover:bg-gray-100 rounded-full" data-testid="events-search-btn">
                  <Search size={20} className="text-gray-600" />
                </button>
              </div>
            </div>
            
            <div className="flex border-b border-gray-100">
              <button 
                onClick={() => setActiveTab('tous')}
                className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${activeTab === 'tous' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-500'}`}
              >
                <Calendar size={14} /> Tous
              </button>
              <button 
                onClick={() => setActiveTab('pro')}
                className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${activeTab === 'pro' ? 'text-[#1E3A5F] border-b-2 border-[#1E3A5F]' : 'text-gray-500'}`}
              >
                <Briefcase size={14} /> Pro
              </button>
              <button 
                onClick={() => setActiveTab('networking')}
                className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${activeTab === 'networking' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-500'}`}
              >
                <Sparkles size={14} /> Networking
              </button>
            </div>
          </header>
        </>
      )}
      
      {/* Contenu */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-500">Découvre les événements de la communauté</p>
          <button onClick={handleCreate} className="flex items-center gap-1.5 px-3 py-2 bg-[#1E3A5F] text-white text-sm font-medium rounded-lg">
            <Plus size={16} /> Créer
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <div className="w-14 h-14 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Calendar size={28} className="text-blue-500" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-2">Aucun événement</h3>
            <p className="text-sm text-gray-500 mb-4">Connecte-toi pour voir et créer des événements</p>
            <Button onClick={() => navigate('/connexion')} className="bg-orange-500 hover:bg-orange-600 text-white text-sm">
              Se connecter
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Liste des événements */}
          </div>
        )}
      </div>
      
      {/* Recherche Fullscreen */}
      <AnimatePresence>
        {showFullscreenSearch && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#ffffff', zIndex: 99999 }}>
            <div className="flex items-center justify-end" style={{ padding: '8px 12px', paddingTop: 'max(8px, env(safe-area-inset-top, 8px))' }}>
              <button onClick={() => setShowFullscreenSearch(false)} style={{ width: '32px', height: '32px' }}>
                <X size={24} className="text-[#64748B]" />
              </button>
            </div>
            
            <div className="px-3 pb-3">
              <form onSubmit={(e) => { e.preventDefault(); handleSearchSubmit(searchQuery); }} className="flex items-center w-full" style={{ height: '56px', border: '2px solid #E8650A', borderRadius: '28px', background: '#F8F9FA', overflow: 'hidden' }}>
                <motion.button type="submit" initial={{ x: 320, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ type: 'spring', stiffness: 280, damping: 24, delay: 0.03 }} className="flex items-center justify-center flex-shrink-0" style={{ width: '56px', height: '56px', background: '#E8650A', borderRadius: '26px 0 0 26px', position: 'relative', left: '-2px' }}>
                  <Search size={24} className="text-white" />
                </motion.button>
                <input ref={searchInputRef} type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Événement, lieu, date..." className="flex-1 bg-transparent text-[#333] placeholder-[#999] focus:outline-none" style={{ fontSize: '16px', paddingLeft: '12px', paddingRight: '16px' }} autoFocus />
                {searchQuery && (
                  <button type="button" onClick={() => setSearchQuery('')} className="flex items-center justify-center mr-4" style={{ width: '22px', height: '22px', background: '#C9CDD4', borderRadius: '50%' }}>
                    <X size={14} className="text-white" />
                  </button>
                )}
              </form>
            </div>
            
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="px-4 py-4">
              <button onClick={() => { setShowFullscreenSearch(false); setTimeout(() => setShowFullFilters(true), 150); }} className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-semibold text-white" style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)', boxShadow: '0 4px 14px rgba(30, 58, 95, 0.25)' }}>
                <SlidersHorizontal size={20} />
                <span style={{ fontSize: '15px' }}>Trouve ton événement en 3 clics</span>
              </button>
            </motion.div>
            
            <div className="p-4 bg-white flex-1 overflow-y-auto">
              <p className="text-[11px] text-[#E8650A] uppercase font-semibold tracking-wide mb-4">Recherche dans : Événements</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-[#9CA3AF] uppercase font-semibold">Recherches récentes</p>
                  {recentSearches.length > 0 && <button onClick={clearRecentSearches} className="text-[11px] text-[#E8650A] font-medium">Effacer</button>}
                </div>
                {recentSearches.length > 0 ? (
                  <div className="space-y-1">
                    {recentSearches.map((search, idx) => (
                      <button key={idx} onClick={() => handleSearchSubmit(search)} className="w-full flex items-center gap-3 py-3 px-2 hover:bg-gray-50 rounded-lg">
                        <Clock size={16} className="text-[#9CA3AF]" />
                        <span className="text-[14px] text-[#374151]">{search}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Search size={32} className="text-[#E5E7EB] mb-3" />
                    <p className="text-[#9CA3AF] text-sm">Aucune recherche récente</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* UnifiedFilterSystem */}
      <UnifiedFilterSystem 
        context="events"
        isOpen={showFullFilters}
        onClose={() => setShowFullFilters(false)}
        filters={activeFilters}
        onFiltersChange={setActiveFilters}
        resultsCount={events.length}
        onApply={() => setShowFullFilters(false)}
      />
      
      {/* MobileBottomNav est géré globalement par MobileNavWrapper dans App.js */}
    </div>
  );
}
